import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  Divider,
  Chip,
} from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import EvStationIcon from "@mui/icons-material/EvStation";
import SyncIcon from "@mui/icons-material/Sync";
import { useStationStats, useImportStations } from "../../../api/stationImport";

export const StationImport: React.FC = () => {
  const { t } = useTranslation();
  const { stats, isLoading: loadingStats } = useStationStats();
  const importMutation = useImportStations();
  const [importSource, setImportSource] = useState<"all" | "fuel" | "ev">("all");

  const handleImport = (source: "all" | "fuel" | "ev") => {
    setImportSource(source);
    importMutation.mutate(source);
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return t("stations.never", "Jamais");
    return new Date(dateStr).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        {t("stations.govImport", "Import Stations Gouvernementales")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t(
          "stations.description",
          "Importez les stations-service (prix-carburants.gouv.fr) et les bornes de recharge électrique (IRVE data.gouv.fr) depuis les API du gouvernement français."
        )}
      </Typography>

      {/* Stats Cards */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        {/* Fuel Stations */}
        <Card sx={{ flex: 1, minWidth: 220 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <LocalGasStationIcon color="warning" />
              <Typography variant="subtitle2" fontWeight={600}>
                {t("stations.fuelStations", "Stations-service")}
              </Typography>
            </Box>
            {loadingStats ? (
              <CircularProgress size={20} />
            ) : (
              <>
                <Typography variant="h4" fontWeight={700} color="warning.main">
                  {stats?.fuel_stations?.toLocaleString("fr-FR") ?? 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("stations.lastSync", "Dernière sync")}: {formatDate(stats?.last_fuel_sync ?? null)}
                </Typography>
              </>
            )}
          </CardContent>
        </Card>

        {/* EV Stations */}
        <Card sx={{ flex: 1, minWidth: 220 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <EvStationIcon color="success" />
              <Typography variant="subtitle2" fontWeight={600}>
                {t("stations.evStations", "Bornes de recharge")}
              </Typography>
            </Box>
            {loadingStats ? (
              <CircularProgress size={20} />
            ) : (
              <>
                <Typography variant="h4" fontWeight={700} color="success.main">
                  {stats?.ev_stations?.toLocaleString("fr-FR") ?? 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("stations.lastSync", "Dernière sync")}: {formatDate(stats?.last_ev_sync ?? null)}
                </Typography>
              </>
            )}
          </CardContent>
        </Card>

        {/* Total */}
        <Card sx={{ flex: 1, minWidth: 220 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <SyncIcon color="primary" />
              <Typography variant="subtitle2" fontWeight={600}>
                {t("stations.totalSigns", "Total Points d'intérêt")}
              </Typography>
            </Box>
            {loadingStats ? (
              <CircularProgress size={20} />
            ) : (
              <Typography variant="h4" fontWeight={700} color="primary.main">
                {stats?.total_signs?.toLocaleString("fr-FR") ?? 0}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Import Actions */}
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
        {t("stations.actions", "Actions d'import")}
      </Typography>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={
            importMutation.isPending && importSource === "all" ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <CloudDownloadIcon />
            )
          }
          disabled={importMutation.isPending}
          onClick={() => handleImport("all")}
          sx={{ textTransform: "none" }}
        >
          {t("stations.importAll", "Importer tout")}
        </Button>

        <Button
          variant="outlined"
          color="warning"
          startIcon={
            importMutation.isPending && importSource === "fuel" ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <LocalGasStationIcon />
            )
          }
          disabled={importMutation.isPending}
          onClick={() => handleImport("fuel")}
          sx={{ textTransform: "none" }}
        >
          {t("stations.importFuel", "Stations-service uniquement")}
        </Button>

        <Button
          variant="outlined"
          color="success"
          startIcon={
            importMutation.isPending && importSource === "ev" ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <EvStationIcon />
            )
          }
          disabled={importMutation.isPending}
          onClick={() => handleImport("ev")}
          sx={{ textTransform: "none" }}
        >
          {t("stations.importEv", "Bornes de recharge uniquement")}
        </Button>
      </Box>

      {/* Source info */}
      <Box sx={{ mt: 3, display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Chip
          label="data.economie.gouv.fr"
          size="small"
          variant="outlined"
          color="warning"
          icon={<LocalGasStationIcon />}
        />
        <Chip
          label="data.gouv.fr (IRVE)"
          size="small"
          variant="outlined"
          color="success"
          icon={<EvStationIcon />}
        />
      </Box>
    </Box>
  );
};
